import { useMemo } from 'react'
import { motion } from 'framer-motion'
import './GlassPanel.css'

/**
 * GlassPanel - A frosted glass container with optional glow border
 * 
 * @param {ReactNode} children - Panel content
 * @param {string} variant - 'default', 'elevated', or 'inset'
 * @param {string} glow - 'none', 'cyan', or 'amber'
 * @param {boolean} hoverable - Whether panel has hover lift effect
 * @param {string} padding - 'none', 'sm', 'md', or 'lg'
 * @param {string} className - Additional CSS classes
 */
export function GlassPanel({
  children,
  variant = 'default',
  glow = 'none',
  hoverable = false,
  padding = 'md',
  className = '',
  as: Component = 'div',
  ...props
}) {
  const panelVariants = {
    idle: { 
      y: 0,
      boxShadow: 'var(--shadow-card)',
    },
    hover: { 
      y: -4,
      boxShadow: 'var(--shadow-card-hover)',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 20,
      },
    },
  }

  // Memoize the motion component to prevent remounting children on every render
  const MotionComponent = useMemo(() => motion(Component), [Component])

  return (
    <MotionComponent
      className={`glass-panel glass-panel--${variant} glass-panel--${padding} glass-panel--glow-${glow} ${className}`}
      variants={hoverable ? panelVariants : undefined}
      initial={hoverable ? "idle" : undefined}
      whileHover={hoverable ? "hover" : undefined}
      {...props}
    >
      {/* Gradient border overlay */}
      {glow !== 'none' && <span className="glass-panel__border" />}
      
      {/* Content */}
      <div className="glass-panel__content">
        {children}
      </div>
    </MotionComponent>
  )
}

export default GlassPanel

